import React, { useState } from "react";
import RetellCallDashboard from "@/test-2/components/voice/RetellCallInterface";
import VoicexCallDashboard from "@/test-2/components/voice/VoicexCallInterface";
import { Agent } from "./Agent";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import BgAgentCard from "./assests/BgAgentCard.png";
import BgAgentCardHover from "./assests/BgAgentCardHover.png";
import CircleCheckmark from "./assests/CircleCheckmark.svg";
import TwoDots from "./assests/TwoDots.svg";
import EnglishUs from "./assests/EnglishUs.svg";
import Hinglish from "./assests/Hinglish.svg";
import EnglishUsHover from "./assests/EnglishUsHover.svg";
import HinglishHover from "./assests/HinglishHover.svg";
import Spanish from "./assests/Spanish.svg";
import SpanishHover from "./assests/SpanishHover.svg";

interface Feature {
  id: number;
  text: string;
}

interface TalkToAgentCardsProps {
  cardTag: string;
  cardTitle: string;
  features: Feature[];
  profilePhoto: string;
  agentsArray: Agent[];
}

const TalkToAgentCards: React.FC<TalkToAgentCardsProps> = ({
  cardTag,
  cardTitle,
  features,
  profilePhoto,
  agentsArray
}) => {
  const [isHovered, setIsHovered] = useState(false);
  // Changed: Individual dialog states for each agent
  const [openDialogs, setOpenDialogs] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [hoveredLang, setHoveredLang] = useState<number | null>(null);
  // Helper function to toggle specific dialog
  const toggleDialog = (index: number, isOpen?: boolean) => {
    setOpenDialogs((prev) => ({
      ...prev,
      [index]: isOpen !== undefined ? isOpen : !prev[index]
    }));
  };

  const [isCallActive, setIsCallActive] = useState(false);

  // Updated: Close specific dialog by index
  const handleEndCall = (index: number) => {
    setIsCallActive(false);
    toggleDialog(index, false);
  };

  const getLanguageInfo = (agent: Agent) => {
    const voices = agent.optional_voices || [];

    for (const voice of voices) {
      const lang = voice.language?.toLowerCase();

      if (lang === "hinglish") {
        return { label: "HINGLISH", flag: "🇮🇳" };
      } else if (lang === "hindi") {
        return { label: "HINGLISH", flag: "🇮🇳" };
      } else if (lang === "spanish") {
        return { label: "SPANISH", flag: "🇪🇸" };
      }
    }

    return { label: "ENGLISH", flag: "🇺🇸" };
  };

  // Updated: Accept index parameter to render specific agent's dashboard
  const renderVoiceDashboard = (index: number) => {
    const selectedConfig = agentsArray[index];
    if (!selectedConfig) return null;

    const dashboardProps = {
      pageCategory: "not defined",
      isDialogOpen: openDialogs[index] || false,
      toggleDialog: () => toggleDialog(index),
      agentName: selectedConfig.name,
      callHints: selectedConfig.callHints,
      callHintsTable: selectedConfig.callHintsTable,
      callTitle: selectedConfig.callTitle || "",
      icon: selectedConfig.icon,
      phoneNumber: selectedConfig.phoneNumber,
      features: selectedConfig.featureList,
      try_speaking: selectedConfig.try_speaking || [],
      activities: selectedConfig.activities || []
    };

    switch (selectedConfig.voiceProvider) {
      case "retell":
        return (
          <RetellCallDashboard
            {...dashboardProps}
            onEndCall={() => handleEndCall(index)}
            agentId={selectedConfig.id}
            voiceId={"default"}
            defaultVoiceId={selectedConfig.default_voiceId}
          />
        );
      case "voicex":
        return (
          <VoicexCallDashboard
            {...dashboardProps}
            voiceConfig={{
              ...selectedConfig.voiceConfig,
              selectedVoiceId: "default" // Example voice ID; replace with actual selection logic,
            }}
            dynamicVariables={{}}
            ttsConfigId={null}
          />
        );

      default:
        return (
          <div className="flex items-center justify-center h-full w-full bg-gray-100">
            <div className="text-center">
              <h2 className="font-inter text-2xl font-semibold mb-4">
                Unsupported Voice Provider
              </h2>
              <p className="font-manrope text-gray-600 mb-4">
                Voice provider "{selectedConfig.voiceProvider}" is not
                supported.
              </p>
              <button
                onClick={() => toggleDialog(index, false)}
                className="font-manrope mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        );
    }
  };

  const handleOpenDialog = (index: number) => {
    setIsHovered(false); // Remove hover as soon as dialog opens
    toggleDialog(index, true);
  };

  return (
    <div className="mx-auto p-2">
      <div
        className="md:w-[365px] md:h-[252px] w-[310px] h-[252px]  border-[1px] border-gray-300 p-6 transition-shadow duration-300 relative overflow-hidden cursor-pointer bg-cover bg-center"
        style={{
          borderRadius: "16px",
          backgroundImage: `url(${isHovered ? BgAgentCardHover : BgAgentCard})`
        }}
        onMouseEnter={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
      >
        {/* Health Insurance Tag */}
        <div className="text-left mb-6 relative z-10">
          <span className="inline-block font-inter px-4 py-2 bg-white text-blue-600 text-sm font-medium rounded-full border border-blue-200 shadow-sm">
            {cardTag}
          </span>
        </div>

        {/* Default Content */}
        <div
          className="absolute left-6 right-6 transition-all duration-500 ease-in-out"
          style={{
            transform: isHovered ? "translateY(-100%)" : "translateY(0)",
            opacity: isHovered ? 0 : 1
          }}
        >
          {/* Title */}
          <h2 className="font-manrope text-left text-3xl text-gray-900 mb-8">
            {cardTitle}
          </h2>

          {/* Profile Image */}
          <div className="flex justify-start mb-8">
            <div className="flex -space-x-2">
              {agentsArray.map((agent, index) => (
                <div
                  key={index}
                  className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center border-2 border-white shadow-sm"
                >
                  <img
                    src={agent?.icon}
                    alt={`AI Agent ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-full transition-colors duration-200 shadow-lg hover:shadow-xl">
            TALK TO AI AGENT{"  "}
            <img src={TwoDots} alt="Two Dots" className="inline-block ml-2" />
          </button>
        </div>

        {/* Hover Content - Features List */}
        <div
          className="absolute left-6 right-6 transition-all duration-500 ease-in-out"
          style={{
            transform: isHovered ? "translateY(0)" : "translateY(100%)",
            opacity: isHovered ? 1 : 0
          }}
        >
          <div className="space-y-4 mb-8">
            <div className="flex items-center space-x-3">
              <img src={CircleCheckmark} alt="Checkmark" className="w-6 h-6" />
              <span className="text-gray-800 font-medium">
                {features[0]?.text}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <img src={CircleCheckmark} alt="Checkmark" className="w-6 h-6" />
              <span className="text-gray-800 font-medium">
                {features[1]?.text}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <img src={CircleCheckmark} alt="Checkmark" className="w-6 h-6" />
              <span className="text-gray-800 font-medium">
                {features[2]?.text}
              </span>
            </div>
          </div>

          {/* Language Selector */}
          <div className="flex flex-wrap gap-3">
            {agentsArray.map((agent, index) => {
              const { label, flag } = getLanguageInfo(agent);
              // Use index to create unique identifiers since agent IDs might be duplicated
              const uniqueAgentId = index;
              const isThisDialogOpen = openDialogs[index] || false;

              return (
                <Dialog
                  open={isThisDialogOpen}
                  onOpenChange={(open) => toggleDialog(index, open)}
                  key={uniqueAgentId}
                >
                  <DialogTrigger asChild>
                    <div
                      className="text-left mt-6 cursor-pointer"
                      onMouseEnter={() => setHoveredLang(index)}
                      onMouseLeave={() => setHoveredLang(null)}
                      onClick={() => {setHoveredLang(null);
                        handleOpenDialog(index);
                        
                      }}
                    >
                      {label === "ENGLISH" && (
                        <img
                          src={hoveredLang === index ? EnglishUsHover : EnglishUs}
                          alt="English US"
                          className={`inline-block mr-2 h-[42px] transition-opacity duration-800 ${
                            hoveredLang === index
                              ? "shadow-[0_4px_16px_0_rgba(0,0,0,0.15)]"
                              : ""
                          }`}
                          style={{ transition: "box-shadow 0.3s", borderRadius: "36px" }}
                        />
                      )}
                      {label === "HINGLISH" && (
                        <img
                          src={hoveredLang === index ? HinglishHover : Hinglish}
                          alt="English US"
                          className={`inline-block mr-2 h-[42px] transition-opacity duration-800 ${
                            hoveredLang === index
                              ? "shadow-[0_4px_16px_0_rgba(0,0,0,0.15)]"
                              : ""
                          }`}
                          style={{ transition: "box-shadow 0.3s", borderRadius: "36px" }}
                        />
                      )}
                      {label === "SPANISH" && (
                        <img
                          src={hoveredLang === index ? SpanishHover : Spanish}
                          alt="Spanish"
                          className={`inline-block mr-2 h-[42px] transition-opacity duration-800 ${
                            hoveredLang === index
                              ? "shadow-[0_4px_16px_0_rgba(0,0,0,0.15)]"
                              : ""
                          }`}
                          style={{ transition: "box-shadow 0.3s", borderRadius: "36px" }}
                        />
                      )}
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-none h-screen w-screen p-0 border-none [&>button]:hidden">
                    {renderVoiceDashboard(index)}
                  </DialogContent>
                </Dialog>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalkToAgentCards;
